import { FC, useState, useCallback } from 'react';

import AccountConfigContext from './AccountConfigContext';
import { IAccountConfig, ICustomerProfile, ICampaignLocation, ICampaignServiceArea } from 'src/lib/schemas';
import { getCustomerProfiles, getLocations, getServiceableAreas } from 'src/services/account';

interface AccountConfigContextProps {
	children: React.ReactNode;
}

const AccountConfigProvider: FC<AccountConfigContextProps> = ({ children }) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [config, setConfig] = useState<IAccountConfig | null>(null);
	const [retries, setRetries] = useState<number>(0);
	const [customerProfiles, setCustomerProfiles] = useState<
		ICustomerProfile[] | null
	>(null);
	const [isLoadingCustomerProfiles, setIsLoadingCustomerProfiles] =
		useState<boolean>(false);
	
	// Locations state
	const [locations, setLocations] = useState<ICampaignLocation[] | null>(null);
	const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(false);
	
	// Service Areas state
	const [serviceAreas, setServiceAreas] = useState<ICampaignServiceArea[] | null>(null);
	const [isLoadingServiceAreas, setIsLoadingServiceAreas] = useState<boolean>(false);

	const handleLoading = (loading: boolean) => {
		if (loading) {
			setRetries(retries + 1);
		}
		setIsLoading(loading);
	};

	const refetchCustomerProfiles = useCallback(async () => {
		setIsLoadingCustomerProfiles(true);
		try {
			const profiles = await getCustomerProfiles();
			setCustomerProfiles(profiles);
		} catch (error) {
			console.error('Error fetching customer profiles:', error);
			setCustomerProfiles([]);
		} finally {
			setIsLoadingCustomerProfiles(false);
		}
	}, []);

	const updateCustomerProfile = useCallback(
		(updatedProfile: ICustomerProfile) => {
			setCustomerProfiles((prevProfiles) => {
				if (!prevProfiles) return [updatedProfile];

				const existingIndex = prevProfiles.findIndex(
					(profile) => profile._id === updatedProfile._id,
				);

				if (existingIndex >= 0) {
					// Update existing profile
					const newProfiles = [...prevProfiles];
					newProfiles[existingIndex] = updatedProfile;
					return newProfiles;
				} else {
					// Profile not found - this shouldn't happen in update case
					console.error(
						'Profile not found for update:',
						updatedProfile._id,
						'Available profiles:',
						prevProfiles.map((p) => p._id),
					);
					return prevProfiles; // Return unchanged instead of adding as new
				}
			});
		},
		[],
	);

	const addCustomerProfile = useCallback((newProfile: ICustomerProfile) => {
		setCustomerProfiles((prevProfiles) => {
			if (!prevProfiles) return [newProfile];

			// Check if profile already exists to prevent duplicates
			const exists = prevProfiles.some(
				(profile) => profile._id === newProfile._id,
			);

			if (exists) {
				console.warn('Profile already exists, updating instead of adding');
				return prevProfiles.map((profile) =>
					profile._id === newProfile._id ? newProfile : profile,
				);
			}

			return [...prevProfiles, newProfile];
		});
	}, []);

	const removeCustomerProfile = useCallback((profileId: string) => {
		setCustomerProfiles((prevProfiles) =>
			prevProfiles
				? prevProfiles.filter((profile) => profile._id !== profileId)
				: [],
		);
	}, []);

	// Locations functions
	const refetchLocations = useCallback(async () => {
		setIsLoadingLocations(true);
		try {
			const data = await getLocations();
			setLocations(data);
		} catch (error) {
			console.error('Error fetching locations:', error);
			setLocations([]);
		} finally {
			setIsLoadingLocations(false);
		}
	}, []);

	const addLocation = useCallback((newLocation: ICampaignLocation) => {
		setLocations((prev) => {
			if (!prev) return [newLocation];
			const locationId = newLocation._id || newLocation.id;
			const exists = prev.some((loc) => (loc._id || loc.id) === locationId);
			if (exists) {
				return prev.map((loc) =>
					(loc._id || loc.id) === locationId ? newLocation : loc,
				);
			}
			return [...prev, newLocation];
		});
	}, []);

	const updateLocationInContext = useCallback((updatedLocation: ICampaignLocation) => {
		setLocations((prev) => {
			if (!prev) return [updatedLocation];
			const locationId = updatedLocation._id || updatedLocation.id;
			return prev.map((loc) =>
				(loc._id || loc.id) === locationId ? updatedLocation : loc,
			);
		});
	}, []);

	const removeLocation = useCallback((locationId: string) => {
		setLocations((prev) =>
			prev ? prev.filter((loc) => (loc._id || loc.id) !== locationId) : [],
		);
	}, []);

	// Service Areas functions
	const refetchServiceAreas = useCallback(async () => {
		setIsLoadingServiceAreas(true);
		try {
			const data = await getServiceableAreas();
			setServiceAreas(data);
		} catch (error) {
			console.error('Error fetching service areas:', error);
			setServiceAreas([]);
		} finally {
			setIsLoadingServiceAreas(false);
		}
	}, []);

	const addServiceArea = useCallback((newServiceArea: ICampaignServiceArea) => {
		setServiceAreas((prev) => {
			if (!prev) return [newServiceArea];
			const serviceAreaId = newServiceArea._id || newServiceArea.id;
			const exists = prev.some((sa) => (sa._id || sa.id) === serviceAreaId);
			if (exists) {
				return prev.map((sa) =>
					(sa._id || sa.id) === serviceAreaId ? newServiceArea : sa,
				);
			}
			return [...prev, newServiceArea];
		});
	}, []);

	const updateServiceAreaInContext = useCallback((updatedServiceArea: ICampaignServiceArea) => {
		setServiceAreas((prev) => {
			if (!prev) return [updatedServiceArea];
			const serviceAreaId = updatedServiceArea._id || updatedServiceArea.id;
			return prev.map((sa) =>
				(sa._id || sa.id) === serviceAreaId ? updatedServiceArea : sa,
			);
		});
	}, []);

	const removeServiceArea = useCallback((serviceAreaId: string) => {
		setServiceAreas((prev) =>
			prev ? prev.filter((sa) => (sa._id || sa.id) !== serviceAreaId) : [],
		);
	}, []);

	return (
		<AccountConfigContext.Provider
			value={{
				isLoading,
				retries,
				setIsLoading: handleLoading,
				config,
				setConfig,
				customerProfiles,
				isLoadingCustomerProfiles,
				setCustomerProfiles,
				setIsLoadingCustomerProfiles,
				refetchCustomerProfiles,
				updateCustomerProfile,
				addCustomerProfile,
				removeCustomerProfile,
				// Locations
				locations,
				isLoadingLocations,
				refetchLocations,
				addLocation,
				updateLocation: updateLocationInContext,
				removeLocation,
				// Service Areas
				serviceAreas,
				isLoadingServiceAreas,
				refetchServiceAreas,
				addServiceArea,
				updateServiceArea: updateServiceAreaInContext,
				removeServiceArea,
			}}
		>
			{children}
		</AccountConfigContext.Provider>
	);
};

export default AccountConfigProvider;
